import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Save, Rocket, LogIn, Wrench } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBuilderLang } from "@/lib/builderStrings";
import { blankConfig, calcApi, normalize, stepErrors, DRAFT_KEY, setActiveCalc } from "@/lib/customCalc";
import { getErrorMessage } from "@/lib/api";
import { track } from "@/lib/analytics";
import Loader from "@/components/Loader";
import StepBasics from "@/components/builder/StepBasics";
import StepClasses from "@/components/builder/StepClasses";
import StepItems from "@/components/builder/StepItems";
import StepMenu from "@/components/builder/StepMenu";
import StepPreview from "@/components/builder/StepPreview";
import StepReview from "@/components/builder/StepReview";

const STEPS = [StepBasics, StepClasses, StepItems, StepMenu, StepReview, StepPreview];
const N = STEPS.length;
const SERVER_STEP = { 5: 3, 6: 5 };

function LangPills({ bl, setBl }) {
  return (
    <div className="flex rounded-full bg-thali-cream p-1 text-xs font-bold" data-testid="b-lang">
      {[["en", "EN"], ["hi", "हिं"], ["or", "ଓଡ଼ି"]].map(([k, v]) => (
        <button key={k} onClick={() => setBl(k)} className={`px-3 py-1.5 rounded-full transition-colors ${bl === k ? "bg-thali-green text-white" : "text-thali-ink/60"}`} data-testid={`b-lang-${k}`}>{v}</button>
      ))}
    </div>
  );
}

export default function CalculatorBuilder({ publicMode = false }) {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const { b, bl, setBl } = useBuilderLang();
  const [cfg, setCfg] = useState(null);
  const [meta, setMeta] = useState(null);
  const [step, setStep] = useState(1);
  const [errs, setErrs] = useState([]);
  const [busy, setBusy] = useState("");
  const [pi, setPi] = useState(0);

  useEffect(() => {
    (async () => {
      try {
        if (id) { const c = await calcApi.get(id); setMeta(c); setCfg(c.config); return; }
        if (sp.get("from") === "odisha") { setCfg(await calcApi.odishaTemplate()); return; }
        const d = localStorage.getItem(DRAFT_KEY);
        setCfg(d ? JSON.parse(d) : blankConfig());
      } catch (e) { toast.error(getErrorMessage(e)); setCfg(blankConfig()); }
    })();
    track("builder_opened", { mode: id ? "edit" : "new" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (cfg && !id) { try { localStorage.setItem(DRAFT_KEY, JSON.stringify(cfg)); } catch { /* noop */ } }
  }, [cfg, id]);

  if (!cfg) return <Loader />;
  const Step = STEPS[step - 1];

  const go = (n) => {
    if (n > step) {
      for (let s = step; s < n; s += 1) {
        const e = stepErrors(cfg, s);
        if (e.length) { setErrs(e); setStep(s); return; }
      }
    }
    setErrs([]); setStep(n); window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const save = async (publish) => {
    for (let s = 1; s < N; s += 1) { const e = stepErrors(cfg, s); if (e.length) { setErrs(e); setStep(s); return; } }
    if (!user) {
      sessionStorage.setItem("pm_after_login", "/app/builder");
      nav("/login");
      return;
    }
    setBusy(publish ? "pub" : "draft");
    try {
      const body = normalize(cfg);
      const c = id ? await calcApi.update(id, body, publish) : await calcApi.create(body, publish);
      localStorage.removeItem(DRAFT_KEY);
      setMeta(c); setCfg(c.config);
      if (publish) {
        toast.success(b("published_ok"), { action: { label: b("use_in_entry"), onClick: () => { setActiveCalc(c.id); nav("/app/entry"); } } });
      } else toast.success(b("saved_ok"));
      if (!id) nav(`/app/builder/${c.id}`, { replace: true });
    } catch (e) {
      const st = e?.response?.data?.detail?.errors?.[0]?.step;
      if (st) setStep(SERVER_STEP[st] || st);
      toast.error(getErrorMessage(e));
    } finally { setBusy(""); }
  };

  return (
    <div className={`space-y-5 pb-40 ${step === 3 ? "max-w-5xl" : "max-w-3xl"} mx-auto ${publicMode ? "px-4 py-6" : ""}`} data-testid="builder-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-extrabold flex items-center gap-2"><Wrench className="w-7 h-7 text-thali-saffron" /> {b("builder")}</h1>
          <p className="text-sm text-thali-ink/60">{b("free_note")}{meta?.version ? ` · ${b("version")}${meta.version} · ${meta.status === "published" ? b("published") : b("draft")}` : ""}</p>
        </div>
        <LangPills bl={bl} setBl={setBl} />
      </div>

      <div>
        <div className="flex items-center justify-between text-xs font-bold text-thali-ink/60 mb-2">
          <span data-testid="b-step-label">{b("step")} {step} {b("of")} {N} · {b(`s${step}`)}</span>
        </div>
        <div className="grid gap-1.5" style={{ gridTemplateColumns: `repeat(${N}, 1fr)` }} data-testid="b-progress">
          {STEPS.map((_, i) => (
            <button key={i} onClick={() => go(i + 1)} aria-label={b(`s${i + 1}`)} data-testid={`b-step-dot-${i + 1}`}
              className={`h-2.5 rounded-full transition-colors ${i + 1 <= step ? "bg-thali-green" : "bg-thali-turmeric/30"}`} />
          ))}
        </div>
      </div>

      <div className="card-thali space-y-4 animate-fade-in-up" key={step}>
        <div>
          <h2 className="font-heading font-extrabold text-lg">{b(`s${step}`)}</h2>
          <p className="text-sm text-thali-ink/60">{b(`s${step}_hint`)}</p>
        </div>
        {errs.length > 0 && (
          <div className="rounded-xl bg-red-50 text-red-700 text-sm p-3 space-y-1" data-testid="b-errors">
            {errs.map((e) => <div key={e}>⚠️ {b(`err_${e}`)}</div>)}
          </div>
        )}
        <Step cfg={cfg} set={(f) => { setErrs([]); setCfg(f); }} b={b} bl={bl} pi={pi} setPi={setPi} goto={go} />
      </div>

      {!user && step === N && (
        <div className="rounded-2xl bg-thali-turmeric/15 border border-thali-turmeric/40 p-3 text-sm" data-testid="b-login-note">🔒 {b("login_note")}</div>
      )}

      <div className={`fixed left-0 right-0 z-50 px-4 ${publicMode ? "bottom-4" : "bottom-[72px] md:bottom-4 md:pl-72"}`} data-testid="b-footer">
        <div className="max-w-3xl mx-auto bg-white/95 backdrop-blur rounded-2xl shadow-2xl border border-thali-turmeric/30 p-2 flex gap-2">
          <button onClick={() => go(step - 1)} disabled={step === 1} className="btn-ghost px-4 disabled:opacity-40" data-testid="b-back"><ChevronLeft className="w-4 h-4" /> {b("back")}</button>
          {step < N ? (
            <button onClick={() => go(step + 1)} className="btn-3d-green flex-1" data-testid="b-next">{b("next")} <ChevronRight className="w-4 h-4" /></button>
          ) : !user ? (
            <button onClick={() => save(false)} className="btn-3d-green flex-1" data-testid="b-login-save"><LogIn className="w-4 h-4" /> {b("login_to_save")}</button>
          ) : (
            <>
              <button onClick={() => save(false)} disabled={!!busy} className="btn-ghost flex-1 border-2 border-thali-green/40 text-thali-green" data-testid="b-save-draft"><Save className="w-4 h-4" /> {busy === "draft" ? b("saving") : b("save_draft")}</button>
              <button onClick={() => save(true)} disabled={!!busy} className="btn-3d-saffron flex-1" data-testid="b-publish"><Rocket className="w-4 h-4" /> {busy === "pub" ? b("saving") : b("publish")}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
